import { APTOS, BUILD_CHAIN_ID, COIN_INFO } from '@/constants';
import { IStorageBasic } from '@/types';
import { TCoinSource } from '@/types/coins';
import { useAptosClient } from '@/composables/useAptosClient';
import { Resource } from '@/libs/aptos';
import { useStore } from '@/store';
import { AptosCoinInfoResource } from '@/types/aptosResources';
import { nope } from '@/utils';
import { composeType, extractAddressFromType } from '@/utils/contracts';
import { aliasForToken, titleForToken } from '@/utils/tokens';
import { useStorage } from '@vueuse/core';
import { defineStore } from 'pinia';
import { computed, ComputedRef, inject, reactive, ref, watch } from 'vue';
import isNumber from 'lodash/isNumber';
import { getFromCache } from '@/utils/cache';
import RestModule from 'ls-api';

export interface IPersistedToken {
  type: string;
  chainId: number;
  decimals: number;
  symbol: string;
  name: string;
  address?: string;
  logo?: string;
  logo_url?: string;
  verified?: boolean;
  source: TCoinSource;
  order?: number;
  caution?: boolean;
  unsafe?: boolean;
  faType?: string;
  isFungibleAsset?: boolean;
}

export interface IPersistedTokenExtended extends IPersistedToken {
  logo: string;
  alias: string;
  title: string;
}

interface IStorage extends IStorageBasic {
  tokens: IPersistedToken[];
}

const PERSISTING_SOURCES = ['import', 'pool'];

export const useTokensStore = defineStore('tokensStore', () => {
  const mainStore = useStore();
  const aptos = useAptosClient();
  const $http = inject('$http') as RestModule['controller'];

  const tokens = reactive<Record<string, IPersistedTokenExtended>>({});
  const isFromLocalStorageLoaded = ref(false);
  const isCoinsListLoaded = ref(false);
  const isRegistered = ref(false);
  const isStoreInitialized = ref(false);
  const isLoading = ref(true);
  const customTokensStorage = useStorage<IStorage>(
    'customTokens',
    { tokens: [], version: 1 },
    window.localStorage,
    { writeDefaults: true },
  );

  /**
   * Storage for all added coins
   */
  const coins = ref<IPersistedToken[]>([
    {
      source: 'pontem',
      name: 'Aptos Coin',
      chainId: BUILD_CHAIN_ID,
      decimals: 8,
      symbol: 'APT',
      type: APTOS,
    },
  ]);

  const isReady = computed(() => {
    return (
      isCoinsListLoaded.value &&
      isFromLocalStorageLoaded.value &&
      isRegistered.value &&
      isStoreInitialized.value
    );
  });

  /**
   * Set to store types of verified tokens
   */
  const verifiedTokensTypes = new Set([APTOS]);

  function processFetchedCoin(item: IPersistedToken) {
    coins.value.push(item);
    if (!item?.caution) {
      verifiedTokensTypes.add(item.type);
    }
  }

  /**
   * Check is token verified
   *
   * @param type token full type
   * @returns boolean true if token verified
   */
  function isTokenVerified(type: string): boolean {
    return verifiedTokensTypes.has(type);
  }

  async function fetchCoinsList() {
    const coins = await getFromCache(
      'coins',
      () =>
        $http.getRegisteredCoins({
          networkId: BUILD_CHAIN_ID,
        }),
      {
        time: 15 * 60 * 1000, // 15 min
      },
    );

    coins.forEach(processFetchedCoin);

    return registerCoins(coins, BUILD_CHAIN_ID);
  }

  async function fetchCoinsData() {
    try {
      isLoading.value = true;
      return Promise.all([loadFromLocalStorage(), fetchCoinsList()]);
    } catch (error) {
      console.error('fetchCoinsData');
    } finally {
      isLoading.value = false;
      isCoinsListLoaded.value = true;
    }
  }

  async function loadFromLocalStorage() {
    if (customTokensStorage.value) {
      try {
        const { tokens } = customTokensStorage.value;
        if (!tokens) return;

        const tokensCandidates = tokens.filter((token) => {
          return PERSISTING_SOURCES.includes(token.source);
        });

        return Promise.all(
          tokensCandidates.map((token) => {
            return loadToken(token);
          }),
        );
      } catch (_e) {
        console.error('tokensStore:loadFromLocalStorage', _e);
      } finally {
        isFromLocalStorageLoaded.value = true;
      }
    }
  }

  function persistCustomToStorage(
    _tokens: Record<string, IPersistedToken>,
    canClean?: boolean,
  ) {
    const customKeys = Object.keys(_tokens).filter((key) =>
      PERSISTING_SOURCES.includes(_tokens[key].source),
    );
    if (!canClean && customKeys.length == 0) return;
    let { version } = customTokensStorage.value;
    // TODO: make an update logic while stored coins structure will be updated
    version = version ?? '1';
    customTokensStorage.value = {
      version,
      tokens: customKeys.map((key) => _tokens[key]),
    };
  }

  watch(tokens, (_tokens) => {
    persistCustomToStorage(_tokens);
  });

  const loadToken = async (token: IPersistedToken) => {
    // - - fetch coinInfo from URL
    const coinInfo = composeType(COIN_INFO, [token.type]);
    const resource =
      await aptos.client.getAccountResource<AptosCoinInfoResource>(
        extractAddressFromType(token.type),
        coinInfo,
      );

    if (!resource) {
      // TODO: Process error
      return undefined;
    }

    const loadingToken: IPersistedToken = {
      type: token.type,
      name: resource.data.name,
      symbol: resource.data.symbol,
      decimals: +resource.data.decimals,
      chainId: token.chainId,
      source: token.source,
      order: token.order,
    };

    return registerToken(loadingToken, { rewrite: true });
  };

  const token: ComputedRef<() => IPersistedToken | undefined> = computed(
    () => (type?: string) => type ? tokens[type] : undefined,
  );

  function waitForTrue(booleanVariable: boolean): Promise<void> {
    return new Promise<void>((resolve) => {
      const interval = setInterval(() => {
        if (booleanVariable) {
          clearInterval(interval);
          resolve();
        }
      }, 100);
    });
  }

  async function asyncGetToken(
    type?: string,
  ): Promise<IPersistedTokenExtended | undefined> {
    try {
      await waitForTrue(!isLoading.value && isReady.value);
      return type && tokens[type] ? tokens[type] : undefined;
    } catch (error) {
      console.error('asyncGetToken', error);
    }
  }

  /**
   * Get an extended token object.
   * The list of tokens is formed from coins-registry/coins and tokens from local storage.
   * @param tokenType token type
   * @returns
   */
  function getToken(tokenType?: string): IPersistedTokenExtended | undefined {
    const type = tokenType?.replace(/^0x0+/, '0x');
    return type && tokens[type] ? tokens[type] : undefined;
  }

  async function registerToken(
    token: IPersistedToken,
    { rewrite = false },
  ): Promise<IPersistedTokenExtended> {
    const { type } = token;
    const _type = type.replace(/^0x0+/, '0x');

    // TODO: Check try to register a duplicate
    if (!rewrite && tokens[type]) {
      if (!tokens[_type].order && isNumber(token?.order)) {
        tokens[_type].order = token.order;
      }
      return tokens[_type];
    }

    if (token?.unsafe) {
      return tokens[_type];
    }

    tokens[_type] = {
      type: _type,
      chainId: token.chainId, // *
      address: token.address, // *
      decimals: +token.decimals, // *
      name: token.name, // *
      symbol: token.symbol, // *
      source: token.source,
      order: token.order || 1000,
      caution: token.caution || false,
      alias: aliasForToken(token),
      title: titleForToken(token),
      logo: token.logo_url || '',
      faType: token.faType,
      isFungibleAsset: token.isFungibleAsset,
    };

    return tokens[_type];
  }

  function searchToken(
    type: string,
    withCancel?: false,
  ): Promise<IPersistedTokenExtended>;
  function searchToken(
    type: string,
    withCancel: true,
  ): {
    request: Promise<IPersistedTokenExtended>;
    cancel: (message?: string) => void;
  };
  function searchToken(type: string, withCancel?: boolean): any {
    const _type = type.replace(/^0x0+/, '0x');
    if (tokens[_type]) {
      return withCancel
        ? {
            cancel: nope,
            request: Promise.resolve(tokens[_type]),
          }
        : Promise.resolve(tokens[_type]);
    }

    const promise = aptos.client.getAccountResource(
      extractAddressFromType(_type),
      composeType(COIN_INFO, [_type]),
      {
        cancelToken: !!withCancel,
      },
    );
    const request = withCancel ? promise.request : promise;

    request.then((resource?: Resource<AptosCoinInfoResource>) => {
      if (!resource) {
        return;
      }

      const { data } = resource;

      return registerToken(
        {
          type: _type,
          name: data.name,
          symbol: data.symbol,
          decimals: +data.decimals,
          chainId: mainStore.networkId.value,
          caution: true,
          source: 'import',
          order: 1000,
        },
        {
          rewrite: false,
        },
      );
    });

    if (withCancel) {
      return {
        request: promise.request,
        cancel: promise.cancel,
      };
    }

    return promise;
  }

  //TODO: APT-420 getTokenInfo func could save received token to store immediately
  async function getTokenInfo(
    token: string,
    remote?: boolean,
  ): Promise<IPersistedToken | undefined> {
    if (tokens[token] && !remote) {
      return tokens[token];
    }

    const coinInfo = composeType(COIN_INFO, [token]);
    const resource =
      await aptos.client.getAccountResource<AptosCoinInfoResource>(
        extractAddressFromType(token),
        coinInfo,
      );

    if (!resource) {
      return undefined;
    }

    return {
      type: token,
      chainId: mainStore.networkId.value,
      name: resource.data.name,
      symbol: resource.data.symbol,
      decimals: +resource.data.decimals,
      caution: true,
      source: 'import',
    };
  }

  const importedTokens: ComputedRef<IPersistedTokenExtended[]> = computed(
    () =>
      Object.keys(tokens)
        .filter((type) => tokens[type].source === 'import') // 'pool', 'import'
        .map((type: string) => getToken(type))
        .filter(Boolean) as IPersistedTokenExtended[],
  );

  function removeToken(token: IPersistedToken) {
    delete tokens[token.type];
    persistCustomToStorage(tokens, true);
  }

  async function registerCoins(list: IPersistedToken[], networkId: number) {
    const registerQueries = list
      .filter((token: IPersistedToken) => token.chainId === networkId)
      .map((token: IPersistedToken) =>
        registerToken(token, {
          // safe: true,
          rewrite: true,
        }),
      );

    return Promise.all(registerQueries);
  }

  function setIsInitialized(value: boolean) {
    isStoreInitialized.value = value;
  }

  watch(
    mainStore.network,
    async (network) => {
      if (!network) {
        return;
      }
      // TODO: Check tokens not valid for network
      await registerCoins(coins.value, network.id);
      isRegistered.value = true;
    },
    { immediate: true },
  );

  return {
    asyncGetToken,
    getToken,
    tokens,
    token,
    registerToken,
    searchToken,
    getTokenInfo,
    importedTokens,
    removeToken,
    isReady,
    isTokenVerified,
    fetchCoinsList,
    fetchCoinsData,
    setIsInitialized,
  };
});
